import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Trophy, Target, Star, Sparkles, Award, TrendingUp, Flame, Zap,
  CheckCircle2, Medal, ArrowRight, BookOpen,
} from 'lucide-react';

// Static badge catalog — pretty render for backend's badge codes.
const BADGE_META = {
  excellence: { ar: 'تميّز', en: 'Excellence', icon: '🏆', color: 'from-amber-400 to-amber-600' },
  default: { ar: 'إنجاز', en: 'Achievement', icon: '⭐', color: 'from-primary to-accent' },
};

function prettyBadge(code) {
  if (!code) return BADGE_META.default;
  if (code.startsWith('mastery:')) {
    return { ar: 'إتقان هدف', en: 'Goal mastery', icon: '🎯', color: 'from-emerald-500 to-emerald-700', sub: code.split(':')[1] };
  }
  return BADGE_META[code] || BADGE_META.default;
}

export default function StudentDashboard() {
  const { t, lang, user } = useApp();
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    api.get('/me/dashboard').then(r => setData(r.data)).catch(() => setData({ children: [], totals: {} }));
    api.get('/analytics/overview').then(r => setOverview(r.data)).catch(() => {});
  }, []);

  if (!data) return <main id="main" className="p-8 text-center text-muted-foreground" data-testid="student-loading">{t('loading')}</main>;

  const totals = data.totals || {};
  const pts = totals.points || 0;
  const level = totals.level || 1;
  const ptsToNext = Math.max(0, level * 100 - pts);
  const nextPercent = Math.min(100, Math.round(((pts % 100) / 100) * 100));
  const certCount = totals.certificate_count || 0;
  const badgeCount = totals.badge_count || 0;

  const children = data.children || [];
  const meStudent = children[0]; // current logged-in student is their own first linked record (typical)
  const allBadges = [...new Set(children.flatMap(c => c.badges || []))];
  const allGoals = children.flatMap(c => (c.ieps || []).flatMap(i => i.goals.map(g => ({ ...g, iep_id: i.id }))));
  const inProgressGoals = allGoals.filter(g => !g.mastered);
  const masteredGoals = allGoals.filter(g => g.mastered);

  // leaderboard rank (from overview)
  let rank = null;
  if (overview && overview.leaderboard && meStudent) {
    const idx = overview.leaderboard.findIndex(s => s.id === meStudent.id);
    if (idx >= 0) rank = idx + 1;
  }

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="student-dashboard">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/8 via-accent/8 to-transparent p-6 sm:p-8 mb-8" data-testid="student-hero">
        <div className="absolute -top-10 -end-10 w-48 h-48 rounded-full bg-accent/10 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-10 -start-10 w-48 h-48 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
        <div className="relative grid lg:grid-cols-3 gap-6 items-center">
          <div className="lg:col-span-2 flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent grid place-items-center text-white font-black text-3xl shadow-xl ring-4 ring-white">
              {user?.full_name?.[0]?.toUpperCase() || 'S'}
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-primary/80 mb-1">{lang === 'ar' ? 'مرحباً بعودتك' : 'Welcome back'}</div>
              <h1 className="text-2xl sm:text-3xl font-black">{user?.full_name}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {meStudent ? `${meStudent.disability_type} · ${meStudent.age_group}` : (lang === 'ar' ? 'لم يتم ربط ملف طالب بعد. تواصل مع معلمك.' : 'No linked student record yet. Contact your teacher.')}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="font-bold text-primary flex items-center gap-1"><Flame className="w-4 h-4" />{lang === 'ar' ? `المستوى ${level}` : `Level ${level}`}</span>
              <span className="font-mono font-black text-2xl text-primary stat-number">{pts}</span>
            </div>
            <Progress value={nextPercent} className="h-3" />
            <div className="text-[11px] text-muted-foreground text-end">
              {lang === 'ar' ? `${ptsToNext > 0 ? ptsToNext : '0'} نقطة للمستوى التالي` : `${ptsToNext > 0 ? ptsToNext : '0'} pts to next level`}
            </div>
          </div>
        </div>

        {/* Mini KPIs */}
        <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <KPI icon={Zap} label={lang === 'ar' ? 'النقاط' : 'Points'} value={pts} color="text-amber-600" testid="kpi-points" />
          <KPI icon={Medal} label={lang === 'ar' ? 'الشارات' : 'Badges'} value={badgeCount} color="text-purple-600" testid="kpi-badges" />
          <KPI icon={Award} label={lang === 'ar' ? 'الشهادات' : 'Certificates'} value={certCount} color="text-emerald-600" testid="kpi-certs" />
          <KPI icon={Trophy} label={lang === 'ar' ? 'الترتيب' : 'Rank'} value={rank ? `#${rank}` : '—'} color="text-rose-600" testid="kpi-rank" />
        </div>
      </section>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Goals to work on */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-primary" />{lang === 'ar' ? 'أهدافي الجارية' : 'My active goals'}</CardTitle>
            <Badge variant="secondary" className="text-xs">{inProgressGoals.length} {lang === 'ar' ? 'هدف' : 'goals'}</Badge>
          </CardHeader>
          <CardContent>
            {inProgressGoals.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500/40" />
                <p className="text-sm">{lang === 'ar' ? 'لا توجد أهداف نشطة. اطلب من معلمك إضافة أهداف جديدة.' : 'No active goals. Ask your teacher to add new ones.'}</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {inProgressGoals.slice(0, 6).map(g => (
                  <div key={`${g.iep_id}-${g.goal_id}`} className="border-2 border-border rounded-xl p-4 card-elevate bg-white" data-testid={`student-lesson-${g.goal_code}`}>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                      <Badge variant="secondary" className="font-mono text-[10px]">{g.goal_code}</Badge>
                      <Badge variant="outline" className="text-[10px]">{lang === 'ar' ? g.sport_ar : (g.sport_en || g.sport_ar)}</Badge>
                    </div>
                    <div className="font-semibold text-sm line-clamp-2 mb-3 min-h-[2.5rem]">
                      {lang === 'ar' ? g.title_ar : (g.title_en || g.title_ar)}
                    </div>
                    <div className="flex items-center justify-between text-[11px] mb-1.5">
                      <span className="text-muted-foreground">{lang === 'ar' ? 'أفضل تقييم' : 'Best score'}</span>
                      <span className="font-bold text-primary">{g.best}%</span>
                    </div>
                    <Progress value={g.best} className="h-2" />
                    <div className="text-[10px] text-muted-foreground mt-2">
                      {g.attempts} {lang === 'ar' ? 'محاولة' : 'attempts'}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {meStudent && (data.children?.[0]?.ieps?.length || 0) > 0 && (
              <div className="mt-4 text-center">
                <Button asChild variant="outline" size="sm" data-testid="see-all-plans">
                  <Link to={`/student-plans/${data.children[0].ieps[0].id}`}>
                    {lang === 'ar' ? 'فتح خطّتي بالكامل' : 'Open my full plan'}
                    <ArrowRight className={`w-4 h-4 ms-1 ${lang === 'ar' ? 'rotate-180' : ''}`} />
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Mastered achievements */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><CheckCircle2 className="w-5 h-5 text-emerald-600" />{lang === 'ar' ? 'إنجازات مُتقَنة' : 'Mastered'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {masteredGoals.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">{lang === 'ar' ? 'لم يتم إتقان أي هدف بعد. هيا!' : 'No goals mastered yet. You can do it!'}</p>
              ) : (
                masteredGoals.slice(0, 4).map(g => (
                  <div key={`m-${g.goal_id}`} className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid={`mastered-${g.goal_code}`}>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold line-clamp-1">{lang === 'ar' ? g.title_ar : (g.title_en || g.title_ar)}</div>
                      <div className="text-[10px] text-emerald-700 font-bold">{g.best}%</div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Badges */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><Medal className="w-5 h-5 text-purple-600" />{lang === 'ar' ? 'شاراتي' : 'My badges'}</CardTitle>
            </CardHeader>
            <CardContent>
              {allBadges.length === 0 ? (
                <p className="text-xs text-muted-foreground py-3 text-center">{lang === 'ar' ? 'أكمل الأهداف لكسب شارات!' : 'Complete goals to earn badges!'}</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {allBadges.slice(0, 6).map((b, i) => {
                    const meta = prettyBadge(b);
                    return (
                      <div key={b} className={`aspect-square rounded-xl bg-gradient-to-br ${meta.color} grid place-items-center text-3xl text-white shadow-md`} title={lang === 'ar' ? meta.ar : meta.en} data-testid={`badge-${i}`}>
                        {meta.icon}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Certificates shortcut */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><Award className="w-5 h-5 text-amber-600" />{lang === 'ar' ? 'شهاداتي' : 'Certificates'}</CardTitle>
            </CardHeader>
            <CardContent>
              {certCount === 0 ? (
                <p className="text-xs text-muted-foreground">{lang === 'ar' ? 'تظهر هنا عند إتقان هدف.' : 'Appear here when you master a goal.'}</p>
              ) : (
                <div className="space-y-2">
                  {(meStudent?.certificates || []).slice(0, 3).map(c => (
                    <div key={c.id} className="border border-amber-200 bg-amber-50 rounded-lg p-2" data-testid={`student-cert-${c.id}`}>
                      <Badge variant="secondary" className="font-mono text-[10px] mb-1">{c.goal_code}</Badge>
                      <div className="text-xs font-semibold line-clamp-2">{c.achievement_ar}</div>
                      <div className="text-[10px] text-emerald-700 font-bold mt-0.5">{c.score}% · {new Date(c.date).toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-US')}</div>
                    </div>
                  ))}
                  {meStudent?.ieps?.[0] && (
                    <Button asChild variant="ghost" size="sm" className="w-full" data-testid="view-all-certs">
                      <Link to={`/student-plans/${meStudent.ieps[0].id}/certificates`}>{lang === 'ar' ? 'عرض كل الشهادات' : 'View all certificates'}</Link>
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick links */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><BookOpen className="w-5 h-5" />{lang === 'ar' ? 'استكشاف' : 'Explore'}</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Button asChild variant="outline" size="sm" className="w-full justify-start" data-testid="explore-curriculum">
                <Link to="/curriculum"><BookOpen className="w-4 h-4 me-2" />{lang === 'ar' ? 'مكتبة المنهج' : 'Curriculum library'}</Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="w-full justify-start" data-testid="explore-ai">
                <Link to="/ai-studio"><Sparkles className="w-4 h-4 me-2" />{lang === 'ar' ? 'استوديو الذكاء الاصطناعي' : 'AI Studio'}</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent activity */}
      {(data.recent_assessments || []).length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" />{lang === 'ar' ? 'آخر التقييمات' : 'Recent assessments'}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2" data-testid="recent-assessments">
              {data.recent_assessments.slice(0, 6).map((a, i) => (
                <li key={a.id || i} className="flex items-center justify-between text-sm border border-border rounded-lg p-2 bg-white">
                  <div className="flex items-center gap-2 min-w-0">
                    <Star className="w-4 h-4 text-primary" />
                    <span className="font-medium line-clamp-1">{a.notes_ar || (lang === 'ar' ? 'تقييم' : 'Assessment')}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant={a.score >= 80 ? 'default' : 'secondary'} className={a.score >= 80 ? 'bg-emerald-600' : ''}>{a.score}%</Badge>
                    <span className="text-[10px] text-muted-foreground">{a.date ? new Date(a.date).toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-US') : ''}</span>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </main>
  );
}

function KPI({ icon: Icon, label, value, color = 'text-primary', testid }) {
  return (
    <div className="bg-white/80 backdrop-blur-sm border border-white/60 rounded-xl p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className={`w-4 h-4 ${color}`} aria-hidden="true" />
      </div>
      <div className={`text-2xl font-black ${color} stat-number`}>{value}</div>
    </div>
  );
}
